import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import OnboardingTour from '@/components/OnboardingTour';
import { Calendar, Briefcase, Users, Sparkles, Plus, UserPlus, MessageSquarePlus, Bell, X, Star, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user, token } = React.useContext(AuthContext);
  const [stats, setStats] = useState({ members: 0, businesses: 0, events: 0, opportunities: 0 });
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentOpportunities, setRecentOpportunities] = useState([]);
  const [communityFeed, setCommunityFeed] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState({ percentage: 0, missing: [] });
  const [spotlightMember, setSpotlightMember] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);
  const [testimonialDialog, setTestimonialDialog] = useState(false);
  const [testimonialText, setTestimonialText] = useState('');
  const [inviteFriendDialog, setInviteFriendDialog] = useState(false);
  const [friendEmail, setFriendEmail] = useState('');
  const [friendName, setFriendName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
    fetchActiveAnnouncements();
    calculateProfileCompletion();
    
    // Load dismissed announcements from localStorage
    const dismissed = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
    setDismissedAnnouncements(dismissed);
  }, []);

  const fetchActiveAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/admin/announcements/all`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      
      // Filter active and non-expired announcements
      const active = response.data.filter(ann => {
        if (!ann.is_active) return false;
        if (ann.expires_at) {
          return new Date(ann.expires_at) > new Date();
        }
        return true;
      });
      setAnnouncements(active);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  };

  const dismissAnnouncement = (announcementId) => {
    const dismissed = [...dismissedAnnouncements, announcementId];
    setDismissedAnnouncements(dismissed);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(dismissed));
  };

  const getAnnouncementColors = (type) => {
    const colors = {
      success: { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-800', icon: 'text-green-600' },
      warning: { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-800', icon: 'text-orange-600' },
      info: { bg: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-800', icon: 'text-blue-600' }
    };
    return colors[type] || colors.info;
  };

  const handleSubmitTestimonial = async () => {
    if (!testimonialText.trim()) {
      toast.error('Please write your testimonial');
      return;
    }

    try {
      const response = await axios.post(`${API}/testimonials/submit`, {
        quote: testimonialText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Testimonial submitted! Awaiting admin approval.');
        setTestimonialDialog(false);
        setTestimonialText('');
      }
    } catch (error) {
      toast.error('Failed to submit testimonial');
      console.error(error);
    }
  };

  const handleInviteFriend = async (e) => {
    e.preventDefault();
    
    if (!friendEmail.trim() || !friendName.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/invite-friend`, {
        friend_name: friendName,
        friend_email: friendEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Try to copy to clipboard, but don't fail if it doesn't work
        try {
          await navigator.clipboard.writeText(response.data.invite_link);
          toast.success(`✉️ Invitation sent to ${friendEmail}!\n\n📋 Invite link copied to clipboard!`, { duration: 5000 });
        } catch (clipboardError) {
          toast.success(`✉️ Invitation email sent to ${friendEmail}!`, { duration: 5000 });
        }
      }
      
      setInviteFriendDialog(false);
      setFriendEmail('');
      setFriendName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
      console.error(error);
    }
  };

  const calculateProfileCompletion = () => {
    const fields = [
      { name: 'business_name', label: 'business name', weight: 25 },
      { name: 'industry', label: 'industry', weight: 25 },
      { name: 'bio', label: 'bio', weight: 25 },
      { name: 'profile_image', label: 'profile photo', weight: 25 }
    ];

    let completed = 0;
    const missing = [];

    fields.forEach(field => {
      if (user?.[field.name] && user[field.name].trim() !== '') {
        completed += field.weight;
      } else {
        missing.push(field.label);
      }
    });

    setProfileCompletion({ percentage: completed, missing });
  };

  const fetchDashboardData = async () => {
    try {
      const [members, businesses, events, opportunities] = await Promise.all([
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/businesses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/opportunities`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats({
        members: members.data.length,
        businesses: businesses.data.length,
        events: events.data.length,
        opportunities: opportunities.data.length
      });

      setRecentEvents(events.data.slice(0, 3));
      setRecentOpportunities(opportunities.data.slice(0, 3));

      // Select spotlight member - prioritize those with businesses
      const membersWithBusiness = members.data.filter(m => m.business_name && m.business_name.trim() !== '');
      if (membersWithBusiness.length > 0) {
        const randomIndex = Math.floor(Math.random() * membersWithBusiness.length);
        setSpotlightMember(membersWithBusiness[randomIndex]);
      } else if (members.data.length > 0) {
        const randomIndex = Math.floor(Math.random() * members.data.length);
        setSpotlightMember(members.data[randomIndex]);
      }

      // Create community feed from recent activities
      const feed = [];
      
      // Add recent members (last 5)
      members.data.slice(-5).reverse().forEach(member => {
        feed.push({
          type: 'member',
          icon: Users,
          color: 'rose',
          message: `${member.full_name} just joined the community${member.industry ? ` from ${member.industry}` : ''}!`,
          timestamp: member.created_at,
          id: member.id
        });
      });

      // Add recent businesses (last 5)
      businesses.data.slice(-5).reverse().forEach(business => {
        feed.push({
          type: 'business',
          icon: Briefcase,
          color: 'pink',
          message: `New business added: ${business.name}${business.industry ? ` (${business.industry})` : ''}`,
          timestamp: business.created_at,
          id: business.id
        });
      });

      // Add recent events (last 5)
      events.data.slice(-5).reverse().forEach(event => {
        feed.push({
          type: 'event',
          icon: Calendar,
          color: 'purple',
          message: `New Event: ${event.title}${event.date ? ` (${new Date(event.date).toLocaleDateString()})` : ''}`,
          timestamp: event.created_at,
          id: event.id
        });
      });

      // Add recent opportunities (last 5)
      opportunities.data.slice(-5).reverse().forEach(opp => {
        feed.push({
          type: 'opportunity',
          icon: Sparkles,
          color: 'orange',
          message: `${opp.type === 'job' ? 'Job Opening' : 'Collaboration'}: ${opp.title}`,
          timestamp: opp.created_at,
          id: opp.id
        });
      });

      // Sort by timestamp (most recent first) and take top 10
      feed.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setCommunityFeed(feed.slice(0, 10));

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getColorClasses = (color) => {
    const colors = {
      rose: 'bg-rose-100 text-rose-600',
      pink: 'bg-pink-100 text-pink-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600'
    };
    return colors[color] || colors.rose;
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <OnboardingTour />
      <div className="container mx-auto px-4 py-8" data-testid="dashboard-page">
        {/* Welcome Banner with Imagery */}
        <div className="mb-8 relative overflow-hidden rounded-2xl glass" data-testid="welcome-banner">
          <div className="grid md:grid-cols-2 gap-6 items-center p-8">
            <div className="z-10">
              <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                Good to see you, {user?.full_name}!
              </h1>
              <p className="text-lg text-gray-700 mb-4">
                Here&apos;s what&apos;s new in your network today.
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="px-4 py-2 bg-rose-100 rounded-full text-rose-700 text-sm font-medium">
                  {stats.members} Members
                </div>
                <div className="px-4 py-2 bg-rose-100 rounded-full text-rose-700 text-sm font-medium">
                  {stats.events} Events
                </div>
                <div className="px-4 py-2 bg-rose-100 rounded-full text-rose-700 text-sm font-medium">
                  {stats.opportunities} Opportunities
                </div>
              </div>
            </div>
            <div className="relative h-64 md:h-80">
              <div className="absolute inset-0 grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-rose-300 to-rose-400 rounded-2xl h-32 flex items-center justify-center shadow-lg animate-float">
                    <Users className="w-16 h-16 text-white opacity-80" />
                  </div>
                  <div className="bg-gradient-to-br from-pink-300 to-pink-400 rounded-2xl h-40 flex items-center justify-center shadow-lg" style={{ animationDelay: '0.5s' }}>
                    <Briefcase className="w-16 h-16 text-white opacity-80" />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="bg-gradient-to-br from-rose-400 to-rose-500 rounded-2xl h-40 flex items-center justify-center shadow-lg" style={{ animationDelay: '0.3s' }}>
                    <Calendar className="w-16 h-16 text-white opacity-80" />
                  </div>
                  <div className="bg-gradient-to-br from-pink-400 to-pink-500 rounded-2xl h-32 flex items-center justify-center shadow-lg animate-float" style={{ animationDelay: '0.7s' }}>
                    <Sparkles className="w-16 h-16 text-white opacity-80" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Active Announcements */}
        {announcements.filter(ann => !dismissedAnnouncements.includes(ann.id)).map((announcement) => {
          const colors = getAnnouncementColors(announcement.type);
          return (
            <Card key={announcement.id} className={`glass mb-6 border-l-4 ${colors.border} ${colors.bg}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 ${colors.icon}`}>
                    <Bell className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className={`font-semibold text-lg mb-1 ${colors.text}`}>
                          {announcement.title}
                        </h3>
                        <p className="text-sm text-gray-700">{announcement.message}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => dismissAnnouncement(announcement.id)}
                        className="ml-4"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Profile Completion Tracker - Only show for regular members, not employees */}
        {!user?.employee_id && profileCompletion.percentage < 100 && (
          <Card className="glass mb-8 border-2 border-rose-200" data-testid="profile-completion">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{profileCompletion.percentage}%</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                      Complete Your Profile
                    </h3>
                    <Button 
                      onClick={() => navigate(`/profile/${user?.id}`)} 
                      variant="outline" 
                      size="sm"
                      className="text-rose-600 border-rose-300 hover:bg-rose-50"
                      data-testid="complete-profile-button"
                    >
                      Update Profile
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Your profile is <span className="font-semibold text-rose-600">{profileCompletion.percentage}% complete</span>
                    {profileCompletion.missing.length > 0 && (
                      <span> - add your {profileCompletion.missing.join(', ')} to reach 100%!</span>
                    )}
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-rose-500 to-pink-500 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${profileCompletion.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Complete Badge */}
        {profileCompletion.percentage === 100 && (
          <Card className="glass mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300" data-testid="profile-complete">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-green-800 mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    🎉 Profile Complete!
                  </h3>
                  <p className="text-sm text-green-700">
                    Excellent! Your profile is 100% complete. You're making the most of the platform!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="mb-8" data-testid="quick-actions">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Quick Actions
          </h2>
          <div className="grid md:grid-cols-5 gap-4">
            <button
              onClick={() => navigate('/businesses')}
              className="glass p-6 rounded-xl hover:shadow-lg transition-all hover:scale-105 cursor-pointer group text-left"
              data-testid="quick-action-business"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center group-hover:bg-rose-200 transition-colors">
                  <Plus className="w-6 h-6 text-rose-600" />
                </div>
                <Briefcase className="w-5 h-5 text-rose-500" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">Add Your Business</h3>
              <p className="text-sm text-gray-600">Showcase your business to the community</p>
            </button>

            <button
              onClick={() => navigate('/events')}
              className="glass p-6 rounded-xl hover:shadow-lg transition-all hover:scale-105 cursor-pointer group text-left"
              data-testid="quick-action-event"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">Create an Event</h3>
              <p className="text-sm text-gray-600">Host a workshop or networking session</p>
            </button>

            <button
              onClick={() => navigate('/opportunities')}
              className="glass p-6 rounded-xl hover:shadow-lg transition-all hover:scale-105 cursor-pointer group text-left"
              data-testid="quick-action-opportunity"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                  <MessageSquarePlus className="w-6 h-6 text-orange-600" />
                </div>
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">Post an Opportunity</h3>
              <p className="text-sm text-gray-600">Share jobs or collaboration opportunities</p>
            </button>

            <Dialog open={testimonialDialog} onOpenChange={setTestimonialDialog}>
              <DialogTrigger asChild>
                <button
                  className="glass p-6 rounded-xl hover:shadow-lg transition-all hover:scale-105 cursor-pointer group text-left"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center group-hover:bg-yellow-200 transition-colors">
                      <Star className="w-6 h-6 text-yellow-600" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1">Share Testimonial</h3>
                  <p className="text-sm text-gray-600">Tell us about your experience</p>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Share Your Testimonial</DialogTitle>
                  <DialogDescription>
                    Tell us about your experience with Her Enterprise Hub. Your testimonial may be featured on our landing page!
                  </DialogDescription>
                </DialogHeader>
                <Textarea
                  placeholder="Share your experience, what you've gained from the community, or how we've helped your business..."
                  value={testimonialText}
                  onChange={(e) => setTestimonialText(e.target.value)}
                  rows={5}
                  className="mt-4"
                />
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleSubmitTestimonial} className="flex-1 bg-rose-600 hover:bg-rose-700">
                    Submit for Review
                  </Button>
                  <Button variant="outline" onClick={() => setTestimonialDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={inviteFriendDialog} onOpenChange={setInviteFriendDialog}>
              <DialogTrigger asChild>
                <button
                  className="glass p-6 rounded-xl hover:shadow-lg transition-all hover:scale-105 cursor-pointer group text-left"
                  data-testid="quick-action-invite"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center group-hover:bg-pink-200 transition-colors">
                      <UserPlus className="w-6 h-6 text-pink-600" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1">Invite a Friend</h3>
                  <p className="text-sm text-gray-600">Grow the community by inviting others</p>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite a Friend to Join</DialogTitle>
                  <DialogDescription>
                    Share Her Enterprise Hub with someone special and help grow our community of empowered women in business.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleInviteFriend} className="space-y-4 mt-4">
                  <Input
                    type="text"
                    placeholder="Friend's Name"
                    value={friendName}
                    onChange={(e) => setFriendName(e.target.value)}
                    required
                  />
                  <Input
                    type="email"
                    placeholder="Friend's Email Address"
                    value={friendEmail}
                    onChange={(e) => setFriendEmail(e.target.value)}
                    required
                  />
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 bg-rose-600 hover:bg-rose-700">
                      Send Invitation
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setInviteFriendDialog(false)} 
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="glass cursor-pointer hover:shadow-lg transition-all" onClick={() => navigate('/members')} data-testid="stats-members">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Members</CardTitle>
              <Users className="w-5 h-5 text-rose-500" />
            </CardHeader>
            <CardContent>
              {stats.members > 0 ? (
                <div className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{stats.members}</div>
              ) : (
                <div>
                  <div className="text-3xl font-bold text-gray-400 mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>0</div>
                  <p className="text-xs text-rose-600 font-medium">Be the first to join!</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass cursor-pointer hover:shadow-lg transition-all" onClick={() => navigate('/businesses')} data-testid="stats-businesses">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Businesses</CardTitle>
              <Briefcase className="w-5 h-5 text-rose-500" />
            </CardHeader>
            <CardContent>
              {stats.businesses > 0 ? (
                <div className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{stats.businesses}</div>
              ) : (
                <div>
                  <div className="text-3xl font-bold text-gray-400 mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>0</div>
                  <p className="text-xs text-rose-600 font-medium">Add yours and inspire!</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass cursor-pointer hover:shadow-lg transition-all" onClick={() => navigate('/events')} data-testid="stats-events">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Events</CardTitle>
              <Calendar className="w-5 h-5 text-rose-500" />
            </CardHeader>
            <CardContent>
              {stats.events > 0 ? (
                <div className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{stats.events}</div>
              ) : (
                <div>
                  <div className="text-3xl font-bold text-gray-400 mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>0</div>
                  <p className="text-xs text-rose-600 font-medium">Host the first event!</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass cursor-pointer hover:shadow-lg transition-all" onClick={() => navigate('/opportunities')} data-testid="stats-opportunities">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Opportunities</CardTitle>
              <Sparkles className="w-5 h-5 text-rose-500" />
            </CardHeader>
            <CardContent>
              {stats.opportunities > 0 ? (
                <div className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{stats.opportunities}</div>
              ) : (
                <div>
                  <div className="text-3xl font-bold text-gray-400 mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>0</div>
                  <p className="text-xs text-rose-600 font-medium">Create one today!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Member Spotlight */}
        {spotlightMember && (
          <Card 
            className="glass mb-8 overflow-hidden cursor-pointer hover:shadow-2xl transition-all border-2 border-rose-200" 
            onClick={() => navigate(`/profile/${spotlightMember.id}`)}
            data-testid="member-spotlight"
          >
            <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-6 py-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-white" />
                <h3 className="text-white font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  ⭐ Member Spotlight
                </h3>
              </div>
            </div>
            <CardContent className="pt-6">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                    {spotlightMember.full_name.charAt(0)}
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                    {spotlightMember.full_name}
                  </h4>
                  {spotlightMember.business_name && (
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-rose-500" />
                      <span className="font-semibold text-gray-800">{spotlightMember.business_name}</span>
                    </div>
                  )}
                  {spotlightMember.industry && (
                    <div className="inline-block px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-medium mb-3">
                      {spotlightMember.industry}
                    </div>
                  )}
                  {spotlightMember.bio && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{spotlightMember.bio}</p>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-rose-600 border-rose-300 hover:bg-rose-50"
                    data-testid="view-spotlight-profile"
                  >
                    View Profile →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Events */}
        <Card className="glass mb-8" data-testid="recent-events">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Upcoming Events</CardTitle>
            <CardDescription>Don't miss these exciting events</CardDescription>
          </CardHeader>
          <CardContent>
            {recentEvents.length > 0 ? (
              <div className="space-y-4">
                {recentEvents.map((event) => (
                  <div key={event.id} className="flex justify-between items-center p-4 rounded-lg bg-white/50" data-testid={`event-${event.id}`}>
                    <div>
                      <h3 className="font-semibold">{event.title}</h3>
                      <p className="text-sm text-gray-600">{event.date} • {event.location}</p>
                    </div>
                    <Button onClick={() => navigate('/events')} variant="outline" size="sm" data-testid={`view-event-${event.id}`}>View</Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8" data-testid="no-events">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-3 font-medium">No events coming up yet</p>
                <p className="text-sm text-gray-500 mb-4">Be the first to bring the community together!</p>
                <Button onClick={() => navigate('/events')} className="bg-rose-500 hover:bg-rose-600" size="sm">
                  Host the First Event
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Opportunities */}
        <Card className="glass" data-testid="recent-opportunities">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Latest Opportunities</CardTitle>
            <CardDescription>Explore new possibilities</CardDescription>
          </CardHeader>
          <CardContent>
            {recentOpportunities.length > 0 ? (
              <div className="space-y-4">
                {recentOpportunities.map((opp) => (
                  <div key={opp.id} className="flex justify-between items-center p-4 rounded-lg bg-white/50" data-testid={`opportunity-${opp.id}`}>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-1 bg-rose-100 text-rose-700 rounded-full">{opp.type}</span>
                        <h3 className="font-semibold">{opp.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600">Posted by {opp.poster_name}</p>
                    </div>
                    <Button onClick={() => navigate('/opportunities')} variant="outline" size="sm" data-testid={`view-opportunity-${opp.id}`}>View</Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8" data-testid="no-opportunities">
                <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-3 font-medium">No opportunities posted yet</p>
                <p className="text-sm text-gray-500 mb-4">Share a job opening or collaboration opportunity!</p>
                <Button onClick={() => navigate('/opportunities')} className="bg-rose-500 hover:bg-rose-600" size="sm">
                  Post First Opportunity
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Community Feed - What's New */}
        <Card className="glass mt-8" data-testid="community-feed">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Community Activity Feed</CardTitle>
            <CardDescription>See what's happening in your network</CardDescription>
          </CardHeader>
          <CardContent>
            {communityFeed.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {communityFeed.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div 
                      key={`${item.type}-${item.id}-${index}`} 
                      className="flex items-start gap-3 p-3 rounded-lg bg-white/40 hover:bg-white/60 transition-colors"
                      data-testid={`feed-item-${index}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getColorClasses(item.color)}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700">{item.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{getTimeAgo(item.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12" data-testid="no-feed-items">
                <div className="flex justify-center gap-2 mb-6">
                  <Users className="w-12 h-12 text-rose-300" />
                  <Briefcase className="w-12 h-12 text-pink-300" />
                  <Calendar className="w-12 h-12 text-purple-300" />
                  <Sparkles className="w-12 h-12 text-orange-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">The community is waiting for you!</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Be the first to make an impact. Add your business, create an event, or post an opportunity.
                </p>
                <div className="flex justify-center gap-3">
                  <Button onClick={() => navigate('/businesses')} variant="outline" size="sm">
                    Add Business
                  </Button>
                  <Button onClick={() => navigate('/opportunities')} className="bg-rose-500 hover:bg-rose-600" size="sm">
                    Post Opportunity
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Floating replay-tour button, shown to non-admin/non-employee members */}
      {user && user.role !== 'admin' && user.role !== 'employee' && (
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('heh:start-tour'))}
          className="fixed bottom-6 left-6 z-30 flex items-center gap-2 px-3 py-2 bg-white/90 backdrop-blur border border-rose-200 rounded-full shadow-md hover:shadow-lg hover:bg-white transition-all text-sm text-rose-800 font-medium"
          title="Replay the welcome tour"
          data-testid="replay-onboarding-tour"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="hidden sm:inline">Take the tour</span>
        </button>
      )}
      <Footer />
    </div>
  );
};

export default Dashboard;